import {
  addDays,
  type AccountDisplayMode,
  calculateGst,
  daysBetweenInclusive,
  todayInTimezone,
  toDecimal,
  yesterdayInTimezone,
} from '@mm/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardCopy,
  History,
  Plus,
  Save,
  Trash2,
  Undo2,
} from 'lucide-react';
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Alert, Badge, Card, EmptyState, PageHeader, Spinner } from '../../components/ui/display';
import { Input } from '../../components/ui/inputs';
import { ConfirmDialog } from '../../components/ui/Modal';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { useToast } from '../../components/ui/Toast';
import { accountLabel } from '../../utils/accounts';
import { formatDate, formatMoney } from '../../utils/format';
import { useAuth } from '../auth/AuthProvider';
import { trimRate } from '../masters/configs';
import { useLookups } from '../../hooks/useLookups';
import { CompletenessBadge } from './CompletenessBadge';
import {
  blankOtherRow,
  initialOtherRows,
  OtherSection,
  type OtherRowState,
  type OtherServerRow,
  otherPreview,
  otherRowStatus,
} from './OtherSection';
import {
  AppCell,
  initialRevenueRows,
  type MorningApp,
  rateToSend,
  RevenueSection,
  revenuePreviewInr,
  revenueRowStatus,
  type RevenueRowState,
  type RevenueServerRow,
} from './RevenueSection';

// ── API shapes ───────────────────────────────────────────────────────

interface EntryRef {
  id: string;
  expenseBeforeGst: string;
  gstRate: string;
  gstAmount: string;
  totalAmount: string;
  updatedAt: string;
}

interface ServerRow {
  /** `${appId}|${accountId}` — one row per app + account assignment. */
  key: string;
  app: MorningApp;
  account: {
    id: string;
    accountName: string;
    accountIdentifier: string | null;
    loginEmail: string | null;
    status: string;
    billingType: 'PREPAID' | 'POSTPAID';
    currencyCode: string;
    platform: { id: string; name: string; code: string };
    managerAccount: { id: string; name: string } | null;
  };
  /** The account was assigned to the app on this date. */
  assigned: boolean;
  active: boolean;
  expected: boolean;
  exchangeRate: string | null;
  entry: EntryRef | null;
  previous: EntryRef | null;
}

interface SectionCompleteness {
  status: 'COMPLETE' | 'PARTIAL' | 'MISSING' | 'NOT_EXPECTED';
  expected: number;
  entered: number;
}

interface MorningData {
  date: string;
  previousDate: string;
  /** GST is calculated automatically at this rate (Settings → Marketing). */
  defaultGstRate: string | null;
  accountDisplayMode: AccountDisplayMode;
  marketing: { rows: ServerRow[] };
  revenue: { rows: RevenueServerRow[] };
  other: { rows: OtherServerRow[]; previous: OtherServerRow[] };
  completeness: { marketing: SectionCompleteness; revenue: SectionCompleteness };
}

interface SaveResult {
  created: number;
  updated: number;
  deleted: number;
  completeness: MorningData['completeness'];
}

const describeStatus = (s: SectionCompleteness['status']) =>
  s === 'COMPLETE' ? 'complete' : s === 'NOT_EXPECTED' ? 'not expected' : s.toLowerCase();

// ── Row state ────────────────────────────────────────────────────────

interface RowState {
  key: string;
  server: ServerRow;
  /** '' means "not entered" — never saved as 0. */
  amount: string;
  hidden: boolean;
  /** A saved entry the user removed; deleted on save. */
  remove: boolean;
}

const AMOUNT_RE = /^\d{1,14}(\.\d{1,4})?$/;

function initialRows(data: MorningData): RowState[] {
  return data.marketing.rows.map((r) => ({
    key: r.key,
    server: r,
    amount: r.entry ? String(Number(r.entry.expenseBeforeGst)) : '',
    hidden: !r.active && !r.entry,
    remove: false,
  }));
}

type RowStatus = 'saved' | 'changed' | 'new' | 'missing' | 'remove' | 'invalid' | 'optional';

function rowStatus(r: RowState): RowStatus {
  const e = r.server.entry;
  if (r.remove) return 'remove';
  if (r.amount !== '' && !AMOUNT_RE.test(r.amount)) return 'invalid';
  if (e) {
    const same = r.amount !== '' && toDecimal(r.amount).equals(e.expenseBeforeGst);
    return same ? 'saved' : r.amount === '' ? 'invalid' : 'changed';
  }
  if (r.amount !== '') return 'new';
  return r.server.expected ? 'missing' : 'optional';
}

const STATUS_UI: Record<
  RowStatus,
  { label: string; tone: 'green' | 'amber' | 'blue' | 'slate' | 'rose' }
> = {
  saved: { label: 'Saved', tone: 'green' },
  changed: { label: 'Changed', tone: 'amber' },
  new: { label: 'New', tone: 'blue' },
  missing: { label: 'Missing', tone: 'rose' },
  optional: { label: 'Not expected', tone: 'slate' },
  remove: { label: 'Will be deleted', tone: 'rose' },
  invalid: { label: 'Check amount', tone: 'rose' },
};

/**
 * Live GST preview with the same Decimal rules the server uses (the server
 * recalculates on save). Saved entries keep their own rate and any invoice GST.
 */
function preview(r: RowState, defaultRate: string) {
  if (r.amount === '' || !AMOUNT_RE.test(r.amount) || r.remove) return null;
  const e = r.server.entry;
  if (e && toDecimal(r.amount).equals(e.expenseBeforeGst)) {
    return { gst: toDecimal(e.gstAmount), total: toDecimal(e.totalAmount) };
  }
  const g = calculateGst(r.amount, e?.gstRate ?? defaultRate);
  return { gst: g.gstAmount, total: g.totalAmount };
}

// ── Page ─────────────────────────────────────────────────────────────

const DAY_ARROW =
  'inline-flex size-10 shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-600 shadow-sm hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40';

export function MorningEntryPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const date = params.get('date') ?? yesterdayInTimezone();
  const today = todayInTimezone();

  const query = useQuery({
    queryKey: ['morning-entry', date],
    queryFn: () => api.get<MorningData>('/morning-entry', { date }),
  });

  const [rows, setRows] = useState<RowState[]>([]);
  const [revRows, setRevRows] = useState<RevenueRowState[]>([]);
  const [otherRows, setOtherRows] = useState<OtherRowState[]>([]);
  const { data: lookups } = useLookups();
  const [showPrevious, setShowPrevious] = useState(false);
  const [saving, setSaving] = useState(false);
  const [duplicates, setDuplicates] = useState<string[] | null>(null);
  const [pendingDate, setPendingDate] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<SaveResult | null>(null);
  const [addKey, setAddKey] = useState('');
  const { can } = useAuth();
  const canOverrideRate = can('fx.override');
  const inputs = useRef<Map<string, HTMLInputElement>>(new Map());

  useEffect(() => {
    if (query.data) {
      setRows(initialRows(query.data));
      setRevRows(initialRevenueRows(query.data.revenue.rows));
      setOtherRows(initialOtherRows(query.data.other.rows));
    }
  }, [query.data]);

  const visible = rows.filter((r) => !r.hidden);
  const statuses = new Map(rows.map((r) => [r.key, rowStatus(r)]));
  const revStatuses = new Map(revRows.map((r) => [r.key, revenueRowStatus(r)]));
  const otherStatuses = new Map(otherRows.map((r) => [r.key, otherRowStatus(r)]));
  const dirty =
    rows.some((r) => ['changed', 'new', 'remove'].includes(statuses.get(r.key)!)) ||
    revRows.some((r) => ['changed', 'new', 'remove'].includes(revStatuses.get(r.key)!)) ||
    otherRows.some((r) => ['changed', 'new', 'remove'].includes(otherStatuses.get(r.key)!));
  const invalid =
    visible.some((r) => statuses.get(r.key) === 'invalid') ||
    revRows.some((r) => !r.hidden && ['invalid', 'needsRate'].includes(revStatuses.get(r.key)!)) ||
    otherRows.some((r) => otherStatuses.get(r.key) === 'invalid');
  const updateOther = (key: string, patch: Partial<OtherRowState>) =>
    setOtherRows((all) => all.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const otherTotals = otherRows.reduce(
    (t, r) => {
      const p = otherPreview(r);
      return p
        ? { before: t.before.plus(p.before), gst: t.gst.plus(p.gst), total: t.total.plus(p.total) }
        : t;
    },
    { before: toDecimal(0), gst: toDecimal(0), total: toDecimal(0) },
  );
  const revenueTotal = revRows
    .filter((r) => !r.hidden)
    .reduce((sum, r) => sum.plus(revenuePreviewInr(r) ?? 0), toDecimal(0));
  const updateRev = (key: string, patch: Partial<RevenueRowState>) =>
    setRevRows((all) => all.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const defaultRate = query.data?.defaultGstRate ?? '18';
  const mode = query.data?.accountDisplayMode ?? 'BOTH';
  const totals = useMemo(() => {
    let before = toDecimal(0);
    let gst = toDecimal(0);
    let total = toDecimal(0);
    for (const r of visible) {
      const p = preview(r, defaultRate);
      if (!p) continue;
      before = before.plus(r.amount);
      gst = gst.plus(p.gst);
      total = total.plus(p.total);
    }
    return { before, gst, total };
  }, [visible, defaultRate]);

  const update = (key: string, patch: Partial<RowState>) =>
    setRows((all) => all.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const changeDate = (next: string) => {
    if (!next || next === date) return;
    if (dirty) setPendingDate(next);
    else setParams({ date: next }, { replace: true });
  };

  /** Copy previous day: bring back yesterday's rows (app + account). Amounts are never copied. */
  const copyPrevious = () => {
    let count = 0;
    setRows((all) =>
      all.map((r) => {
        if (!r.server.previous || !r.server.assigned) return r;
        count++;
        return { ...r, hidden: false };
      }),
    );
    setRevRows((all) =>
      all.map((r) => (r.server.previous && r.server.assigned ? { ...r, hidden: false } : r)),
    );
    // Other expenses: yesterday's rows (category, app, rate) are added once, without amounts.
    const prevOther = (query.data?.other.previous ?? []).filter((p) => p.currencyCode === 'INR');
    setOtherRows((all) => {
      const fresh = prevOther.filter(
        (p) => !all.some((r) => r.categoryId === p.categoryId && (r.appId || null) === p.appId),
      );
      return [
        ...all,
        ...fresh.map((p) =>
          blankOtherRow(defaultRate, {
            categoryId: p.categoryId,
            appId: p.appId ?? '',
            gstRate: trimRate(p.gstRate),
          }),
        ),
      ];
    });
    setShowPrevious(true);
    toast.info(
      count
        ? `Copied ${count} row(s) from ${formatDate(query.data!.previousDate)}. Enter today's numbers.`
        : 'No marketing entries on the previous day.',
    );
  };

  /** Rows the user hid (or inactive ones with an assignment) can be brought back. */
  const addable = rows.filter((r) => r.hidden && r.server.assigned);
  const addRow = (key: string) => {
    setAddKey('');
    if (!key) return;
    update(key, { hidden: false, remove: false });
    requestAnimationFrame(() => inputs.current.get(key)?.focus());
  };

  const focusNext = (key: string, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const order = visible.map((r) => r.key);
    const i = order.indexOf(key) + (e.key === 'ArrowUp' ? -1 : 1);
    const next = order[i];
    if (next) inputs.current.get(next)?.select();
  };

  const save = async (overwriteDuplicates = false) => {
    if (!query.data) return;
    setSaving(true);
    setDuplicates(null);
    try {
      const marketing = rows
        .filter((r) => !r.hidden && !r.remove && ['changed', 'new'].includes(statuses.get(r.key)!))
        .map((r) => ({
          appId: r.server.app.id,
          advertisingAccountId: r.server.account.id,
          expenseBeforeGst: r.amount,
          entryId: r.server.entry?.id,
          updatedAt: r.server.entry?.updatedAt,
        }));
      const deleteMarketingIds = rows
        .filter((r) => r.remove && r.server.entry)
        .map((r) => r.server.entry!.id);
      const revenue = revRows
        .filter(
          (r) => !r.hidden && !r.remove && ['changed', 'new'].includes(revStatuses.get(r.key)!),
        )
        .map((r) => ({
          appId: r.server.app.id,
          revenueAccountId: r.server.account.id,
          revenueAmount: r.amount,
          exchangeRate: rateToSend(r),
          entryId: r.server.entry?.id,
          updatedAt: r.server.entry?.updatedAt,
        }));
      const deleteRevenueIds = revRows
        .filter((r) => r.remove && r.server.entry)
        .map((r) => r.server.entry!.id);
      const other = otherRows
        .filter((r) => !r.remove && ['changed', 'new'].includes(otherStatuses.get(r.key)!))
        .map((r) => ({
          entryId: r.server?.id,
          updatedAt: r.server?.updatedAt,
          categoryId: r.categoryId,
          appId: r.appId || null,
          amountBeforeGst: r.amount,
          gstRate: r.gstRate || trimRate(defaultRate),
        }));
      const deleteOtherIds = otherRows.filter((r) => r.remove && r.server).map((r) => r.server!.id);
      const result = await api.post<SaveResult>('/morning-entry', {
        date,
        marketing,
        revenue,
        other,
        deleteMarketingIds,
        deleteRevenueIds,
        deleteOtherIds,
        overwriteDuplicates,
      });
      setLastSaved(result);
      toast.success(
        `Saved ${formatDate(date)}: ${result.created} new, ${result.updated} updated, ${result.deleted} deleted`,
      );
      await queryClient.invalidateQueries({ queryKey: ['morning-entry', date] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['marketing'] });
      void queryClient.invalidateQueries({ queryKey: ['revenue'] });
      void queryClient.invalidateQueries({ queryKey: ['other-expenses'] });
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        err.errors.some((e) => /already has an entry|Save anyway/.test(e.message))
      ) {
        setDuplicates(err.errors.map((e) => e.message));
      } else {
        toast.error(
          err instanceof ApiError
            ? `${err.message}${err.errors[0] ? ` — ${err.errors[0].message}` : ''}`
            : 'Saving failed',
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const data = query.data;

  return (
    <div className="pb-32 sm:pb-40">
      <PageHeader
        title="Morning data entry"
        description="Enter yesterday's numbers. Leave a box empty if you haven't checked it yet; enter 0 when there was nothing."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1">
              <button
                type="button"
                className={DAY_ARROW}
                aria-label="Previous day"
                title="Previous day"
                onClick={() => changeDate(addDays(date, -1))}
              >
                <ChevronLeft className="size-4" />
              </button>
              <Input
                type="date"
                aria-label="Transaction date"
                value={date}
                max={today}
                onChange={(e) => changeDate(e.target.value)}
                className="w-44"
              />
              <button
                type="button"
                className={DAY_ARROW}
                aria-label="Next day"
                title={date >= today ? 'No later day' : 'Next day'}
                disabled={date >= today}
                onClick={() => changeDate(addDays(date, 1))}
              >
                <ChevronRight className="size-4" />
              </button>
            </div>
            {date !== yesterdayInTimezone() && (
              <Button variant="ghost" size="sm" onClick={() => changeDate(yesterdayInTimezone())}>
                Yesterday
              </Button>
            )}
          </div>
        }
      />

      {query.isLoading && <Spinner />}
      {query.isError && (
        <Alert tone="error">
          {query.error instanceof ApiError ? query.error.message : 'Failed to load'}
        </Alert>
      )}

      {data && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
            <span className="font-medium text-slate-900">{formatDate(data.date)}</span>
            <span className="flex items-center gap-1.5">
              Marketing <CompletenessBadge status={data.completeness.marketing.status} />
              <span className="text-slate-400">
                {data.completeness.marketing.entered}/{data.completeness.marketing.expected}
              </span>
            </span>
            <span className="flex items-center gap-1.5">
              Revenue <CompletenessBadge status={data.completeness.revenue.status} />
              <span className="text-slate-400">
                {data.completeness.revenue.entered}/{data.completeness.revenue.expected}
              </span>
            </span>
            {date !== yesterdayInTimezone() && (
              <span className="text-slate-400">
                ({date === today ? 'today' : `${daysBetweenInclusive(date, today) - 1} days ago`})
              </span>
            )}
          </div>

          {lastSaved && !dirty && (
            <Alert tone="success" title="All data saved">
              {lastSaved.created} new, {lastSaved.updated} updated, {lastSaved.deleted} deleted. For{' '}
              {formatDate(date)}: marketing is{' '}
              {describeStatus(lastSaved.completeness.marketing.status)}, revenue is{' '}
              {describeStatus(lastSaved.completeness.revenue.status)}.
            </Alert>
          )}

          <Card
            title="1. Marketing spend"
            description={`Spend before GST for each app and the account it used. GST (${trimRate(defaultRate)}%) is added automatically.`}
            bodyClassName="p-0"
            actions={
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<History className="size-4" />}
                  onClick={() => setShowPrevious((s) => !s)}
                >
                  {showPrevious ? 'Hide previous day' : 'Load previous day'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<ClipboardCopy className="size-4" />}
                  onClick={copyPrevious}
                >
                  Copy previous day
                </Button>
              </>
            }
          >
            {visible.length === 0 ? (
              <EmptyState
                title="No ads accounts assigned"
                description="Assign Google Ads or other ads accounts to your apps (on each app's page) to enter spend here."
              />
            ) : (
              <div className="relative overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                    <tr>
                      <th className="px-4 py-3 text-left">App</th>
                      <th className="px-4 py-3 text-left">Account</th>
                      <th className="px-4 py-3 text-right">Spend (₹, before GST)</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-2 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {visible.map((r) => {
                      const st = statuses.get(r.key)!;
                      const a = r.server.account;
                      return (
                        <tr
                          key={r.key}
                          className={clsx(
                            r.remove && 'bg-rose-50/60 text-slate-400 line-through',
                            st === 'missing' && 'bg-amber-50/30',
                          )}
                        >
                          <td className="px-4 py-2">
                            <AppCell app={r.server.app} />
                          </td>
                          <td className="px-4 py-2">
                            <div className="text-slate-900">{accountLabel(a, mode)}</div>
                            <div className="text-xs text-slate-400">
                              {[
                                a.platform.name,
                                a.billingType === 'POSTPAID'
                                  ? `Credit · ${a.managerAccount?.name ?? ''}`
                                  : 'Prepaid',
                              ].join(' · ')}
                            </div>
                          </td>
                          <td className="px-4 py-2 text-right">
                            <input
                              ref={(el) => {
                                if (el) inputs.current.set(r.key, el);
                                else inputs.current.delete(r.key);
                              }}
                              aria-label={`Spend for ${r.server.app.name} ${a.accountName}`}
                              inputMode="decimal"
                              autoComplete="off"
                              disabled={r.remove}
                              value={r.amount}
                              placeholder="—"
                              onChange={(e) =>
                                update(r.key, { amount: e.target.value.replace(/,/g, '').trim() })
                              }
                              onKeyDown={(e) => focusNext(r.key, e)}
                              onFocus={(e) => e.target.select()}
                              className={clsx(
                                'num h-9 w-36 rounded-md border px-2 text-right focus:ring-2 focus:outline-none',
                                st === 'invalid'
                                  ? 'border-rose-400 focus:ring-rose-200'
                                  : 'border-slate-300 focus:border-brand-500 focus:ring-brand-500/20',
                              )}
                            />
                            {showPrevious && (
                              <div className="num mt-0.5 text-xs text-slate-400">
                                Prev:{' '}
                                {r.server.previous
                                  ? formatMoney(r.server.previous.expenseBeforeGst)
                                  : 'no entry'}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            <Badge tone={STATUS_UI[st].tone}>{STATUS_UI[st].label}</Badge>
                          </td>
                          <td className="px-2 py-2 text-right">
                            {r.server.entry ? (
                              <button
                                type="button"
                                onClick={() => update(r.key, { remove: !r.remove })}
                                className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                                aria-label={r.remove ? 'Keep saved entry' : 'Delete saved entry'}
                                title={r.remove ? 'Keep saved entry' : 'Delete saved entry'}
                              >
                                {r.remove ? (
                                  <Undo2 className="size-4" />
                                ) : (
                                  <Trash2 className="size-4" />
                                )}
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => update(r.key, { hidden: true, amount: '' })}
                                className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                                aria-label="Remove row"
                                title="Remove row (nothing is saved)"
                              >
                                <Trash2 className="size-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {addable.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-4 py-3">
                <Plus className="size-4 text-slate-400" />
                <SearchableSelect
                  ariaLabel="Add row"
                  className="w-full sm:w-96"
                  placeholder="Add row — app and account…"
                  value={addKey}
                  options={addable.map((r) => ({
                    value: r.key,
                    label: `${r.server.app.name} — ${accountLabel(r.server.account, mode)}`,
                  }))}
                  onChange={addRow}
                />
              </div>
            )}
          </Card>

          <RevenueSection
            rows={revRows}
            statuses={revStatuses}
            showPrevious={showPrevious}
            canOverrideRate={canOverrideRate}
            onChange={updateRev}
          />
          <OtherSection
            rows={otherRows}
            statuses={otherStatuses}
            lookups={lookups}
            defaultGstRate={trimRate(defaultRate)}
            onChange={updateOther}
            onAdd={() => setOtherRows((all) => [...all, blankOtherRow(trimRate(defaultRate))])}
            onDiscard={(key) => setOtherRows((all) => all.filter((r) => r.key !== key))}
          />

          {/* Sticky summary + Save All. Phones and tablets show only marketing, revenue and
              marketing profit; the full breakdown is added on large screens. */}
          <div className="fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white/95 backdrop-blur transition-[left] duration-200 lg:left-64 lg:in-data-[sidebar=collapsed]:left-16">
            <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-x-8 gap-y-2 px-4 py-2 sm:px-6 sm:py-3 lg:px-8">
              <div className="grid w-full grid-cols-3 gap-3 sm:flex sm:w-auto sm:gap-8">
                <Summary label="Marketing" value={formatMoney(totals.before.toString())} />
                <Summary label="Revenue" value={formatMoney(revenueTotal.toString())} />
                <Summary
                  label="Marketing profit"
                  value={formatMoney(revenueTotal.minus(totals.before).toString())}
                  tone={revenueTotal.minus(totals.before).isNegative() ? 'loss' : 'profit'}
                />
              </div>
              <div className="hidden flex-wrap items-center gap-x-8 gap-y-2 lg:flex">
                <Summary
                  label="Marketing GST (auto)"
                  value={formatMoney(totals.gst.toString())}
                  tone="gst"
                />
                <Summary
                  label="Other expenses"
                  value={formatMoney(otherTotals.before.toString())}
                />
                <Summary
                  label="GST (input credit)"
                  value={formatMoney(totals.gst.plus(otherTotals.gst).toString())}
                  tone="gst"
                />
                <Summary
                  label="Cash outflow"
                  value={formatMoney(totals.total.plus(otherTotals.total).toString())}
                />
                <Summary
                  label="Operating profit"
                  value={formatMoney(
                    revenueTotal.minus(totals.before).minus(otherTotals.before).toString(),
                  )}
                  tone={
                    revenueTotal.minus(totals.before).minus(otherTotals.before).isNegative()
                      ? 'loss'
                      : 'profit'
                  }
                />
              </div>
              <div className="ml-auto flex items-center gap-3">
                {dirty ? (
                  <span className="text-xs text-amber-600">Unsaved changes</span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-emerald-600">
                    <CheckCircle2 className="size-3.5" /> All saved
                  </span>
                )}
                <Button
                  icon={<Save className="size-4" />}
                  loading={saving}
                  disabled={!dirty || invalid}
                  onClick={() => void save()}
                >
                  SAVE ALL DATA
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={duplicates !== null}
        title="Some accounts already have data"
        tone="primary"
        confirmLabel="Overwrite"
        loading={saving}
        message={
          <div className="space-y-2">
            <p>Someone already entered data for these rows on {formatDate(date)}:</p>
            <ul className="list-disc pl-5">
              {duplicates?.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
            <p>
              Overwrite them with the values on this screen? The change is recorded in the audit
              log.
            </p>
          </div>
        }
        onConfirm={() => void save(true)}
        onCancel={() => setDuplicates(null)}
      />
      <ConfirmDialog
        open={pendingDate !== null}
        title="Discard unsaved changes?"
        message="You have changes on this date that are not saved yet."
        confirmLabel="Discard and switch date"
        onConfirm={() => {
          if (pendingDate) setParams({ date: pendingDate }, { replace: true });
          setPendingDate(null);
          setLastSaved(null);
        }}
        onCancel={() => setPendingDate(null)}
      />
    </div>
  );
}

function Summary({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: string;
  tone?: 'gst' | 'profit' | 'loss';
  hint?: string;
}) {
  return (
    <div>
      <p className="text-[11px] font-medium tracking-wide text-slate-500 uppercase">{label}</p>
      <p
        className={clsx(
          'num text-base font-semibold',
          tone === 'gst'
            ? 'text-violet-700'
            : tone === 'profit'
              ? 'text-emerald-700'
              : tone === 'loss'
                ? 'text-rose-700'
                : 'text-slate-900',
        )}
      >
        {value}
        {hint && <span className="ml-1 text-[10px] font-normal text-slate-400">{hint}</span>}
      </p>
    </div>
  );
}
